const TRACE_HEADER = "x-wr-trace";
const STORAGE_KEY = "wr-ct";
const MAX_AGE_MS = 20 * 60 * 60 * 1000;
const AGENT_URL = "https://fpjscdn.net/v4";

type TRegister = (requestId: string) => Promise<void>;

type TSession = {
  userId: string;
  register: TRegister;
};

type TStoredTrace = {
  id: string;
  at: number;
  userId: string | null;
};

interface IAgent {
  get: () => Promise<{ event_id?: string }>;
}

interface IAgentModule {
  start: (options?: { region: string }) => Promise<IAgent>;
}

const isStoredTrace = (value: unknown): value is TStoredTrace => {
  const trace = value as Partial<TStoredTrace> | null;

  return (
    typeof trace?.id === "string" &&
    typeof trace.at === "number" &&
    (typeof trace.userId === "string" || trace.userId === null)
  );
};

class ClientTraceService {
  private memory: TStoredTrace | null = null;
  private inflight: Promise<void> | null = null;
  private failed = false;
  private session: TSession | null = null;

  private get publicKey(): string | undefined {
    return process.env.NEXT_PUBLIC_FP_PUBLIC_KEY || undefined;
  }

  private get region(): string | undefined {
    return process.env.NEXT_PUBLIC_FP_REGION || undefined;
  }

  headers(): Record<string, string> {
    const trace = this.fresh();

    if (!trace) {
      if (!this.failed) {
        void this.refresh();
      }

      return {};
    }

    return { [TRACE_HEADER]: trace.id };
  }

  ensure(userId: string, register: TRegister): Promise<void> {
    this.session = { userId, register };

    return this.refresh();
  }

  private refresh(): Promise<void> {
    const { session } = this;

    if (typeof window === "undefined" || !this.publicKey || !session) {
      return Promise.resolve();
    }

    if (!this.inflight) {
      this.inflight = this.sync(session)
        .catch(() => undefined)
        .finally(() => {
          this.inflight = null;
        });
    }

    return this.inflight;
  }

  private async sync({ userId, register }: TSession): Promise<void> {
    let trace = this.fresh();

    if (!trace) {
      const id = await this.mint();

      if (!id) {
        return;
      }

      trace = { id, at: Date.now(), userId: null };
      this.write(trace);
    }

    if (trace.userId === userId) {
      return;
    }

    await register(trace.id);
    this.write({ ...trace, userId });
  }

  private async mint(): Promise<string | null> {
    if (this.failed) {
      return null;
    }

    try {
      const agentModule: IAgentModule = await import(
        /* webpackIgnore: true */ `${AGENT_URL}/${this.publicKey}`
      );
      const agent = await agentModule.start(this.region ? { region: this.region } : undefined);
      const { event_id: eventId } = await agent.get();

      if (typeof eventId === "string" && eventId.length > 0) {
        return eventId;
      }
    } catch {
      this.failed = true;

      return null;
    }

    this.failed = true;

    return null;
  }

  private fresh(): TStoredTrace | null {
    if (typeof window === "undefined") {
      return null;
    }

    const trace = this.read();
    const age = trace ? Date.now() - trace.at : -1;

    return trace && age >= 0 && age < MAX_AGE_MS ? trace : null;
  }

  private read(): TStoredTrace | null {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;

      return isStoredTrace(parsed) ? parsed : this.memory;
    } catch {
      return this.memory;
    }
  }

  private write(trace: TStoredTrace): void {
    this.memory = trace;

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trace));
    } catch {}
  }
}

const clientTrace = new ClientTraceService();

export default clientTrace;
export { ClientTraceService };
